/**
 * Pure logic for the Admin CRM "Import ingredients JSON" flow (no DOM, no Firebase), so it can be unit tested.
 * Schema: docs/master_ingredients_schema.md
 */

/** Keys an imported ingredient may use (including the legacy aliases the importer has always accepted). */
const KNOWN_INPUT_KEYS = new Set([
  "uid", "id", "ingredient_id", "name", "display_name", "category",
  "subgroup_code", "subgroup_name", "canonical_unit", "display_unit", "plural", "canonical_quantity",
  "is_pantry_staple", "is_perishable", "pack_size", "price_tier", "allergen_flags", "allergens", "dietary_preferences", "substitutes", "updated_at",
  "image_path", "imageUrl", "image_url"
]);

/** Proxy price bands for the meal-plan cost gauge (#482). Docs: `docs/master_ingredients_schema.md`. */
const VALID_PRICE_TIERS = new Set(["$", "$$", "$$$", "$$$$"]);

const snake = (value) => String(value).toLowerCase().replace(/[^a-z0-9_]/g, "_");

/**
 * Parses an uploaded ingredients payload and normalizes every ingredient to the documented schema.
 *
 * Accepted shapes: an array of ingredients, `{ ingredients: { uid: {...} } }`, `{ ingredients: [...] }`, or a plain
 * `{ uid: {...} }` map. A root-level `image_url_prefix` (the shared CDN prefix stored once on the document, so it is not
 * repeated on every ingredient) is returned as `image_url_prefix`.
 *
 * @param {string} rawText
 * @param {(unit: string) => string} getPluralUnit
 * @returns {{ error: string } | { ingredients: Object, count: number, image_url_prefix: (string|null), ignoredFields: string[] }}
 */
export function parseAndNormalizeIngredientsJson(rawText, getPluralUnit = (unit) => unit) {
  if (!rawText || !rawText.trim()) {
    return { error: "JSON input is empty." };
  }
  let parsed;
  try {
    parsed = JSON.parse(rawText);
  } catch (err) {
    return { error: `Invalid JSON syntax: ${err.message}` };
  }

  if (!parsed || typeof parsed !== "object") {
    return { error: "Root JSON must be an object or array." };
  }

  const isRootObject = !Array.isArray(parsed);
  const rootPrefix = isRootObject && typeof parsed.image_url_prefix === "string" && parsed.image_url_prefix.trim()
    ? parsed.image_url_prefix.trim()
    : null;

  let itemsMap = {};
  if (Array.isArray(parsed)) {
    parsed.forEach((item) => {
      if (item && typeof item === "object") {
        const uid = item.uid || item.id || item.ingredient_id || (item.name ? item.name.toLowerCase().replace(/[^a-z0-9_]/g, "_") : null);
        if (uid) {
          itemsMap[uid] = item;
        }
      }
    });
  } else if (parsed.ingredients && typeof parsed.ingredients === "object" && !Array.isArray(parsed.ingredients)) {
    itemsMap = parsed.ingredients;
  } else if (Array.isArray(parsed.ingredients)) {
    parsed.ingredients.forEach((item) => {
      if (item && typeof item === "object") {
        const uid = item.uid || item.id || item.ingredient_id;
        if (uid) itemsMap[uid] = item;
      }
    });
  } else {
    // Plain object map: { "chicken_breast": { ... }, ... }
    Object.keys(parsed).forEach((k) => {
      if (k !== "count" && k !== "updated_at" && k !== "image_url_prefix" && typeof parsed[k] === "object" && parsed[k] !== null) {
        itemsMap[k] = parsed[k];
      }
    });
  }

  const keys = Object.keys(itemsMap);
  if (keys.length === 0) {
    return { error: "No valid ingredient objects found in JSON." };
  }

  const ignored = new Set();
  const normalized = {};
  for (const k of keys) {
    const rawItem = itemsMap[k];
    Object.keys(rawItem).forEach((field) => {
      if (!KNOWN_INPUT_KEYS.has(field)) ignored.add(field);
    });

    const uid = snake(rawItem.uid || rawItem.id || k);
    const displayName = rawItem.display_name || rawItem.name || k;
    const subgroupCode = snake(rawItem.subgroup_code || rawItem.category || "pantry");
    const subgroupName = rawItem.subgroup_name || subgroupCode.replace(/_/g, " ");
    const canonicalUnit = (rawItem.canonical_unit || "").toLowerCase() === "ml" ? "ml" : "gram";
    const displayUnit = rawItem.display_unit || canonicalUnit;
    const pluralUnit = rawItem.plural || getPluralUnit(displayUnit);
    const canonicalQuantity = Number(rawItem.canonical_quantity || 1);
    // Same fallbacks as the ingredient editor. Only the differentiator slug (or a full URL) is stored per ingredient;
    // the shared CDN prefix lives once at the document root.
    const imagePath = String(rawItem.image_path || rawItem.imageUrl || rawItem.image_url || "").trim();

    normalized[uid] = {
      uid,
      display_name: displayName,
      display_unit: displayUnit,
      plural: pluralUnit,
      canonical_unit: canonicalUnit,
      canonical_quantity: canonicalQuantity,
      is_pantry_staple: Boolean(rawItem.is_pantry_staple),
      // Tri-state on purpose: a missing value means "unknown" (excluded from waste counting), never "false".
      ...(typeof rawItem.is_perishable === "boolean" ? { is_perishable: rawItem.is_perishable } : {}),
      // Optional retail pack size in canonical units (grams or ml). Only a positive number is kept; anything else is dropped.
      ...(typeof rawItem.pack_size === "number" && Number.isFinite(rawItem.pack_size) && rawItem.pack_size > 0 ? { pack_size: rawItem.pack_size } : {}),
      // Optional cost-gauge proxy tier (#482). A missing or unrecognized value means unpriced, never a guessed tier.
      ...(VALID_PRICE_TIERS.has(rawItem.price_tier) ? { price_tier: rawItem.price_tier } : {}),
      subgroup_code: subgroupCode,
      subgroup_name: subgroupName,
      allergen_flags: Array.isArray(rawItem.allergen_flags) ? rawItem.allergen_flags : (Array.isArray(rawItem.allergens) ? rawItem.allergens : []),
      dietary_preferences: Array.isArray(rawItem.dietary_preferences) ? rawItem.dietary_preferences : [],
      substitutes: Array.isArray(rawItem.substitutes) ? rawItem.substitutes : [],
      ...(imagePath ? { image_path: imagePath } : {}),
      updated_at: rawItem.updated_at || new Date().toISOString()
    };
  }

  return {
    ingredients: normalized,
    count: Object.keys(normalized).length,
    image_url_prefix: rootPrefix,
    ignoredFields: [...ignored].sort()
  };
}

/** Deterministic JSON so two ingredients with the same content compare equal regardless of key order. */
function stableStringify(value) {
  if (Array.isArray(value)) return `[${value.map(stableStringify).join(",")}]`;
  if (value && typeof value === "object") {
    return `{${Object.keys(value).sort().map((k) => `${JSON.stringify(k)}:${stableStringify(value[k])}`).join(",")}}`;
  }
  return JSON.stringify(value);
}

const withoutTimestamp = (item) => {
  const { updated_at: _ignored, ...rest } = item || {};
  return rest;
};

/**
 * What replacing `current` with `next` would do, so the admin can see it before overwriting production data.
 * Timestamps are ignored: an ingredient is "updated" only if some other field differs (or is dropped).
 */
export function summarizeCatalogChange(current = {}, next = {}) {
  const added = [];
  const updated = [];
  const removed = [];
  let unchanged = 0;

  for (const uid of Object.keys(next)) {
    if (!(uid in current)) {
      added.push(uid);
    } else if (stableStringify(withoutTimestamp(current[uid])) !== stableStringify(withoutTimestamp(next[uid]))) {
      updated.push(uid);
    } else {
      unchanged++;
    }
  }
  for (const uid of Object.keys(current)) {
    if (!(uid in next)) removed.push(uid);
  }

  return { added, updated, removed, unchanged };
}

/** Firestore documents are limited to 1 MiB; leave headroom for field names and overhead. */
export const MAX_CATALOG_BYTES = 1_000_000;

export function estimatedDocumentBytes(payload) {
  return new TextEncoder().encode(JSON.stringify(payload)).length;
}
